import {
  Autocomplete,
  Button,
  CircularProgress,
  FormControl,
  TextField,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import React, { useEffect, useState } from 'react'
import { useAuthContext } from '../../../hooks/useAuthContext'
import { fieldTemplateService } from '../../../services/fieldTemplateService'
import { GroupHeader, GroupItems, textFieldStyle } from './CreateProcessTemplateDialog'
import { processTemplateService } from '../../../services/processTemplateService'
import { listDataService } from '../../../services/listDataService'
import { FIELD_TYPES } from '../../../utility/constants'
import { toast } from 'react-toastify'
import { adminTeamsService } from '../../../services/admin/teams'
import { Delete } from '@mui/icons-material'

const ALL_OPTION = { id: '__ALL__', name: 'All', value: 'All' }

const FieldVisibilitySetting = ({ processTemplate, setSelectedProcess, setShow }) => {
  const { user } = useAuthContext()
  const [fields, setFields] = useState([])
  const [lists, setLists] = useState([])
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState(null)
  const [rows, setRows] = useState([{ field1: null, field2: [], field3: [] }])
  const [listData, setListData] = useState([])
  const [teams, setTeams] = useState([])
  const [deptData, setDeptData] = useState([])

  useEffect(() => {
    ;(async () => {
      setLoading(true)
      if (processTemplate?.id) {
        const allFields = await fieldTemplateService.getDerivableFieldsForProcess(
          user,
          processTemplate?.id,
        )
        setFields(allFields)

        const lists = await listDataService.getListOfListsForAdmin(user)
        setLists(lists)

        const teamData = await adminTeamsService.getTeams(user)
        setTeams(teamData)

        const dashboardData = await processTemplateService.getProcessDashboardData(
          user,
          processTemplate.id,
        )
        const departmentListData = dashboardData.deptListDataId

        let deptListData = []
        if (departmentListData) {
          deptListData = (await listDataService.getData(user, departmentListData))?.data
          setDeptData(deptListData)
        } else {
          setDeptData([])
        }

        let loadedListData = []
        if (dashboardData.visibilitySettings?.identifier_field_template_id) {
          setData({
            identifier_field_template_id:
              dashboardData.visibilitySettings.identifier_field_template_id,
            identifier_field_template_name:
              dashboardData.visibilitySettings.identifier_field_template_name,
          })

          const selectedField = allFields.find(
            (f) => f.id === dashboardData.visibilitySettings.identifier_field_template_id,
          )
          if (selectedField?.list_id) {
            loadedListData = (await listDataService.getData(user, selectedField.list_id))?.data
            setListData(loadedListData)
          }
        }

        if (dashboardData.visibilitySettings?.visibility_settings?.length > 0) {
          const populatedRows = dashboardData.visibilitySettings.visibility_settings.map(
            (setting) => {
              const matchedValue =
                loadedListData.find((item) => item.id === setting.value?.id) || setting.value

              const matchedTeams = (setting.teams || [])
                .map((team) => teamData.find((t) => t.id === team.id))
                .filter(Boolean)

              const matchedDepts = (setting.departments || [])
                .map((dept) => deptListData.find((d) => d.id === dept.id))
                .filter(Boolean)

              return {
                field1: matchedValue,
                field2: matchedTeams,
                field3: matchedDepts,
              }
            },
          )

          setRows(populatedRows)
        }
      }
      setLoading(false)
    })()
  }, [processTemplate?.id, user])

  const onSave = async () => {
    try {
      if (!data?.identifier_field_template_id) {
        toast.error('Please select a Field.')
        return
      }

      const hasValidRow = rows.some(
        (row) =>
          row.field1 &&
          ((Array.isArray(row.field2) && row.field2.length > 0) ||
            (Array.isArray(row.field3) && row.field3.length > 0)),
      )

      if (!hasValidRow) {
        toast.error('Please add and fill at least one row with all required fields.')
        return
      }

      for (let i = 0; i < rows.length; i++) {
        const row = rows[i]
        const isEmptyRow =
          !row.field1 &&
          (!row.field2 || row.field2.length === 0) &&
          (!row.field3 || row.field3.length === 0)
        if (!isEmptyRow) {
          if (
            !row.field1 ||
            ((!row.field2 || row.field2.length === 0) && (!row.field3 || row.field3.length === 0))
          ) {
            toast.error(
              `Please fill all required fields for row ${i + 1}. At least one of Teams or Departments must be selected.`,
            )
            return
          }
        }
      }

      const payload = {
        ...data,
        visibility_settings: rows.map((row) => ({
          value: row.field1,
          teams: row.field2,
          departments: row.field3,
        })),
      }

      await processTemplateService.saveDashboardVisibilitySettings(
        user,
        processTemplate.id,
        payload,
      )

      toast.success('Dashboard Visibility setting saved successfully.')
    } catch (error) {
      console.error('Error saving dashboard visibility:', error)
      toast.error(error.message || 'Something went wrong while saving.')
    }
  }

  const handleDelete = async () => {
    try {
      if (!data?.identifier_field_template_id) {
        toast.error('No visibility settings to delete.')
        return
      }
      await processTemplateService.deleteDashboardVisibilitySettings(
        user,
        processTemplate.id,
        data.identifier_field_template_id,
      )

      // Reset the form after successful deletion
      setData(null)
      setRows([{ field1: null, field2: [], field3: [] }])
      setListData([])

      toast.success('Dashboard Visibility settings deleted successfully.')
    } catch (error) {
      console.error('Error deleting dashboard visibility:', error)
      toast.error(error.message || 'Something went wrong while deleting.')
    }
  }

  const handleAddRow = () => {
    setRows((prev) => [...prev, { field1: null, field2: [], field3: [] }])
  }

  const handleDeleteRow = (idx) => {
    setRows((prev) => prev.filter((_, i) => i !== idx))
  }

  if (loading) {
    return (
      <div
        style={{
          height: '40vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress color="primary" size={40} />
      </div>
    )
  }

  return (
    <div>
      <Typography>Process Dashboard Visibility </Typography>
      <FormControl
        variant="outlined"
        fullWidth
        size="small"
        color="primary"
        style={{ marginTop: 16 }}
      >
        <Autocomplete
          id="select-derived-field"
          options={fields
            .filter((it) => it.data_structure === FIELD_TYPES.LIST && !it.is_subfield)
            .sort((a, b) => -b.activity_template_name.localeCompare(a.activity_template_name))}
          groupBy={(option) => option?.activity_template_name}
          size="small"
          getOptionLabel={(item) => item?.field_template_name}
          value={fields.find((it) => it.id === data?.identifier_field_template_id) || null}
          onChange={async (_, newValue) => {
            setData({
              ...data,
              identifier_field_template_id: newValue?.id,
              identifier_field_template_name: newValue?.field_template_name,
            })
            if (newValue?.list_id) {
              const response = (await listDataService.getData(user, newValue.list_id))?.data
              setListData(response)
            } else {
              setListData([])
            }
          }}
          renderGroup={(params) => (
            <li key={params.key}>
              <GroupHeader>{params.group}</GroupHeader>
              <GroupItems>{params.children}</GroupItems>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              label="Select Process Identifier Field"
              color="primary"
            />
          )}
          sx={textFieldStyle}
        />
      </FormControl>

      {/* Render dynamic rows */}
      {rows.map((row, idx) => (
        <div
          key={idx}
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr 40px',
            gap: 12,
            marginTop: 16,
            alignItems: 'center',
          }}
        >
          <Autocomplete
            size="small"
            options={listData || []}
            getOptionLabel={(option) => option.value || ''}
            value={row.field1}
            onChange={(_, value) => {
              setRows((prev) => {
                const updated = [...prev]
                updated[idx] = { ...updated[idx], field1: value }
                return updated
              })
            }}
            renderInput={(params) => <TextField {...params} label="Select Value" />}
            disablePortal
          />

          <Autocomplete
            multiple
            size="small"
            options={[ALL_OPTION, ...(teams || [])]}
            getOptionLabel={(option) => option.name || ''}
            value={row.field2 || []}
            onChange={(_, value) => {
              setRows((prev) => {
                const updated = [...prev]
                const isAllSelected = value.some((v) => v.id === ALL_OPTION.id)
                updated[idx] = {
                  ...updated[idx],
                  field2: isAllSelected ? teams : value.filter((v) => v.id !== ALL_OPTION.id),
                }
                return updated
              })
            }}
            renderInput={(params) => <TextField {...params} label="Select Team" />}
            disablePortal
          />

          <Autocomplete
            multiple
            size="small"
            options={[ALL_OPTION, ...(deptData || [])]}
            getOptionLabel={(option) => option.value || ''}
            value={row.field3 || []}
            onChange={(_, value) => {
              setRows((prev) => {
                const updated = [...prev]
                const isAllSelected = value.some((v) => v.id === ALL_OPTION.id)
                updated[idx] = {
                  ...updated[idx],
                  field3: isAllSelected ? deptData : value.filter((v) => v.id !== ALL_OPTION.id),
                }
                return updated
              })
            }}
            renderInput={(params) => <TextField {...params} label="Select Department" />}
            disablePortal
          />

          <Tooltip title="Delete Row" placement="bottom">
            <IconButton
              aria-label="delete"
              sx={{ color: '#656D92' }}
              onClick={() => handleDeleteRow(idx)}
              size="small"
            >
              <Delete />
            </IconButton>
          </Tooltip>
        </div>
      ))}

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          marginTop: 24,
        }}
      >
        <Tooltip title="Add Row" placement="left">
          <IconButton sx={{ color: '#656D92' }} onClick={handleAddRow}>
            <AddIcon />
          </IconButton>
        </Tooltip>
      </div>

      <div
        style={{
          marginTop: 24,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {/* Delete button on the left */}
        {data?.identifier_field_template_id && (
          <Button color="primary" variant="contained" onClick={handleDelete} startIcon={<Delete />}>
            Delete All
          </Button>
        )}

        {/* Existing buttons on the right */}
        <div style={{ display: 'flex', columnGap: 16, marginLeft: 'auto' }}>
          <Button
            variant="outlined"
            onClick={() => {
              setSelectedProcess(null)
              setShow(false)
            }}
          >
            Cancel
          </Button>
          <Button color="primary" variant="contained" onClick={onSave}>
            Save
          </Button>
        </div>
      </div>
    </div>
  )
}

export default FieldVisibilitySetting
